import type {
  ParametersLocation,
  ParametersUnit,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesThreshold,
  ParameterTimeSeriesThresholdColor,
} from "@/lib/api";
import {
  hasObservedTimeSeriesData,
  isObservedTimeSeriesLoadFailure,
} from "@/lib/api/observed-time-series-for-location.utils";
import type { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import { formatParameterDisplayValue } from "@/lib/parameters/format-parameter-display-value";
import { resolveFetchUnitForParameter } from "@/lib/parameters/parameter-units.utils";

import { datapointAtTimelineTime } from "./derive-stepped-time-range-from-datapoints";
import type {
  LocationParameterSeriesRequest,
  LocationParameterTimeSeriesResult,
} from "./hooks/queries";

export const MARKER_NO_DATA = "—";

export type LocationMarkerLoadState = "loading" | "ready";

export type ParameterSeriesQuerySnapshot = {
  isPending: boolean;
  isError: boolean;
  data?: LocationParameterTimeSeriesResult;
};

/** A cell as rendered inside a marker row (values, gaps and error/no-data). */
export type MarkerRowCell =
  | { kind: "unsupported" }
  | { kind: "error"; parameterLabel: string }
  | { kind: "noData" }
  | { kind: "value"; text: string; thresholdColor?: ParameterTimeSeriesThresholdColor }
  | { kind: "stationName"; text: string };

/**
 * Raw per-parameter cell. Wind direction/speed carry extra data used to build
 * the layout (the arrow degrees); they are normalized to {@link MarkerRowCell}
 * value cells before rendering.
 */
export type MarkerParameterCell =
  | MarkerRowCell
  | {
      kind: "windDirection";
      degrees: number;
      text: string;
      thresholdColor?: ParameterTimeSeriesThresholdColor;
    }
  | { kind: "windSpeed"; text: string; thresholdColor?: ParameterTimeSeriesThresholdColor };

/** One unit group: a single row of value cells (config order preserved). */
export type MarkerRow = { unitKey: string; cells: MarkerRowCell[] };

export type MarkerDisplayState =
  | { phase: "loading" }
  | { phase: "ready"; rows: MarkerRow[]; windDegrees?: number };

export type MarkerSeriesAggregateStatus = {
  anyPending: boolean;
  anyError: boolean;
};

function fmtWindDirectionMarkerLabel(
  unit: ParametersUnit | undefined,
  value: string | number,
): string {
  const parsedValue = Number(value);
  const rounded = Number.isNaN(parsedValue) ? value : Math.round(parsedValue);
  return `${rounded}${unit?.symbol ?? ""}`;
}

/** Marker bubble labels; rain uses two decimals. Knots and cloud base in feet use integers. */
export function fmtMarkerDisplayText(
  unit: ParametersUnit | undefined,
  value: string | number,
  parameterId: string,
): string {
  if (unit?.code === "knot") {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return `${Math.round(parsedValue)}${unit?.symbol ?? ""}`;
    }
  }

  if (parameterId.includes("cloud-base-altitude") && unit?.code === "foot") {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      return `${Math.round(parsedValue)}${unit?.symbol ?? ""}`;
    }
  }

  const formatted = formatParameterDisplayValue(value, unit);
  if (formatted !== null) return formatted;
  return `${value}${unit?.symbol ?? ""}`;
}

export function locationSupportsParameter(
  location: ParametersLocation,
  parameterId: string,
): boolean {
  return location.parameter_ids.includes(parameterId);
}

export function findSeriesRequestIndex(
  requests: LocationParameterSeriesRequest[],
  locationId: string,
  parameterId: string,
  unitCode: string | undefined,
): number {
  return requests.findIndex(
    (request) =>
      request.locationId === locationId &&
      request.parameterId === parameterId &&
      (unitCode === undefined || request.unitCode === unitCode),
  );
}

/** True for observed wind-from direction parameters (degrees, arrow UI). */
export function isWindDirectionParameter(
  parameterId: string,
  unit: ParametersUnit | undefined,
): boolean {
  if (unit?.code !== "degree") return false;
  return parameterId.includes("wind-direction");
}

/** Unit codes we treat as wind speed (knots, m/s). */
const WIND_SPEED_UNIT_CODES = ["knot", "meter_per_second", "metre_per_second"];

/** True for observed wind-speed parameters carrying a speed unit (knots, m/s). */
export function isWindSpeedParameter(
  parameterId: string,
  unit: ParametersUnit | undefined,
): boolean {
  if (!unit?.code || !WIND_SPEED_UNIT_CODES.includes(unit.code)) return false;
  return parameterId.includes("wind-speed");
}

/**
 * Group parameter cells by unit into rows. Walking the parameters in config
 * order: the first parameter of a unit opens a new row (group), and every later
 * parameter sharing that unit is appended to the existing row. Group order is
 * the order each unit is first seen; cell order within a group is config order.
 *
 * Wind cells are normalized to plain value cells here because the arrow is drawn
 * once, to the left of all rows: the first wind-direction cell's degrees are
 * returned as `windDegrees` for that arrow, while its label still renders in the
 * (degrees) row like any other value. Any threshold color carries through.
 */
export function groupMarkerCellsByUnit(
  entries: { unitKey: string; cell: MarkerParameterCell }[],
): { rows: MarkerRow[]; windDegrees?: number } {
  const cellsByUnit = new Map<string, MarkerRowCell[]>();
  const unitOrder: string[] = [];
  let windDegrees: number | undefined;

  for (const { unitKey, cell } of entries) {
    let rowCell: MarkerRowCell;
    if (cell.kind === "windDirection") {
      if (windDegrees === undefined) windDegrees = cell.degrees;
      rowCell = { kind: "value", text: cell.text, thresholdColor: cell.thresholdColor };
    } else if (cell.kind === "windSpeed") {
      rowCell = { kind: "value", text: cell.text, thresholdColor: cell.thresholdColor };
    } else {
      rowCell = cell;
    }

    let cells = cellsByUnit.get(unitKey);
    if (!cells) {
      cells = [];
      cellsByUnit.set(unitKey, cells);
      unitOrder.push(unitKey);
    }
    cells.push(rowCell);
  }

  const rows = unitOrder.map((unitKey) => ({ unitKey, cells: cellsByUnit.get(unitKey)! }));
  return windDegrees === undefined ? { rows } : { rows, windDegrees };
}

/** Unit code of a settled series result, when it carries time-series data. */
function seriesUnitCode(
  query: ParameterSeriesQuerySnapshot | undefined,
): string | undefined {
  const result = query?.data;
  if (!hasObservedTimeSeriesData(result)) return undefined;
  return result.data.unit?.code;
}

/** Thresholds of the time-series group that contains this parameter (empty if none). */
export function findThresholdsForParameter(
  parameterId: string,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): ParameterTimeSeriesThreshold[] {
  const config = timeSeriesConfigurations.find((configuration) =>
    configuration.series.some((series) => series.parameter_id === parameterId),
  );
  return config?.thresholds ?? [];
}

/**
 * Resolves the color of the most severe active threshold for a value, or undefined when
 * none are breached. Max thresholds win over min (a single value cannot sensibly breach
 * both), and within a type the most extreme breached value is chosen.
 */
export function resolveActiveThresholdColor(
  value: number,
  thresholds: ParameterTimeSeriesThreshold[],
): ParameterTimeSeriesThresholdColor | undefined {
  if (thresholds.length === 0 || Number.isNaN(value)) return undefined;

  let activeMax: ParameterTimeSeriesThreshold | undefined;
  let activeMin: ParameterTimeSeriesThreshold | undefined;

  for (const threshold of thresholds) {
    if (threshold.threshold_type === "max" && value > threshold.value) {
      if (!activeMax || threshold.value > activeMax.value) activeMax = threshold;
    }
    if (threshold.threshold_type === "min" && value < threshold.value) {
      if (!activeMin || threshold.value < activeMin.value) activeMin = threshold;
    }
  }

  return (activeMax ?? activeMin)?.color;
}

export function getMarkerParameterCellState(
  parameterId: string,
  location: ParametersLocation,
  query: ParameterSeriesQuerySnapshot | undefined,
  currentTime: TimeRangeDimensionValue | undefined,
  resolveParameterLabel: (parameterId: string) => string | undefined,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[] = [],
): MarkerParameterCell {
  if (!locationSupportsParameter(location, parameterId)) {
    return { kind: "unsupported" };
  }
  if (!query || query.isPending) {
    return { kind: "noData" };
  }
  if (query.isError) {
    return markerLoadErrorCell(parameterId, resolveParameterLabel);
  }
  if (!query.data || !hasObservedTimeSeriesData(query.data)) {
    return { kind: "noData" };
  }

  const timeSeries = query.data.data;
  const point = datapointAtTimelineTime(timeSeries.datapoints, currentTime?.timestamp);
  const value = point?.value;
  if (value === undefined || value === null) {
    return { kind: "noData" };
  }
  if (typeof value !== "number" && typeof value !== "string") {
    return {
      kind: "value",
      text: `${value}${timeSeries.unit?.symbol ?? ""}`,
    };
  }

  const unit = timeSeries.unit;
  const numericValue = typeof value === "number" ? value : Number(value);
  const thresholdColor = Number.isNaN(numericValue)
    ? undefined
    : resolveActiveThresholdColor(
        numericValue,
        findThresholdsForParameter(parameterId, timeSeriesConfigurations),
      );

  if (isWindDirectionParameter(query.data.parameter_id, unit)) {
    if (!Number.isNaN(numericValue)) {
      return {
        kind: "windDirection",
        degrees: numericValue,
        text: fmtWindDirectionMarkerLabel(unit, value),
        thresholdColor,
      };
    }
  }

  if (isWindSpeedParameter(query.data.parameter_id, unit)) {
    return {
      kind: "windSpeed",
      text: fmtMarkerDisplayText(unit, value, parameterId),
      thresholdColor,
    };
  }

  return {
    kind: "value",
    text: fmtMarkerDisplayText(unit, value, parameterId),
    thresholdColor,
  };
}

/**
 * Marker content for the active display group: parameters grouped into rows by
 * unit (see {@link groupMarkerCellsByUnit}), plus the wind arrow direction.
 */
export function getMarkerDisplayState(
  location: ParametersLocation,
  markerParameterIds: string[],
  requests: LocationParameterSeriesRequest[],
  queries: ParameterSeriesQuerySnapshot[],
  currentTime: TimeRangeDimensionValue | undefined,
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
  resolveParameterLabel: (parameterId: string) => string | undefined,
): MarkerDisplayState {
  if (markerParameterIds.length === 0) {
    return {
      phase: "ready",
      rows: [
        { unitKey: "station-name", cells: [{ kind: "stationName", text: location.name }] },
      ],
    };
  }

  let anySupportedPending = false;

  for (const parameterId of markerParameterIds) {
    if (!locationSupportsParameter(location, parameterId)) continue;

    const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
    const index = findSeriesRequestIndex(requests, location.id, parameterId, unitCode);
    const query = index >= 0 ? queries[index] : undefined;
    if (!query || query.isPending) {
      anySupportedPending = true;
      break;
    }
  }

  if (anySupportedPending) {
    return { phase: "loading" };
  }

  const entries = markerParameterIds.map((parameterId) => {
    const fetchUnitCode = resolveFetchUnitForParameter(
      parameterId,
      timeSeriesConfigurations,
    );
    const query =
      queries[findSeriesRequestIndex(requests, location.id, parameterId, fetchUnitCode)];
    const cell = getMarkerParameterCellState(
      parameterId,
      location,
      query,
      currentTime,
      resolveParameterLabel,
      timeSeriesConfigurations,
    );
    // Group by the value's actual unit; fall back to the configured fetch unit,
    // then the parameter id so unit-less parameters never merge with each other.
    const unitKey = seriesUnitCode(query) ?? fetchUnitCode ?? parameterId;
    return { unitKey, cell };
  });

  return { phase: "ready", ...groupMarkerCellsByUnit(entries) };
}

/**
 * True unless a settled marker has every slot as a dash (no-data / unsupported).
 * Loading markers count as having data (neutral) so they are not de-prioritized
 * or shown as no-data dots while their series are still streaming in.
 */
export function markerHasData(state: MarkerDisplayState): boolean {
  if (state.phase !== "ready") return true;
  return state.rows.some((row) =>
    row.cells.some((cell) => cell.kind !== "noData" && cell.kind !== "unsupported"),
  );
}

/** True when the location supports at least one chart-configured parameter. */
export function locationSupportsAnyConfiguredParameter(
  location: ParametersLocation,
  configuredParameterIds: string[],
): boolean {
  if (configuredParameterIds.length === 0) return true;
  return configuredParameterIds.some((parameterId) =>
    locationSupportsParameter(location, parameterId),
  );
}

/** Pending while any supported parameter across all groups is still fetching. */
export function getLocationMarkerLoadState(
  location: ParametersLocation,
  configuredParameterIds: string[],
  requests: LocationParameterSeriesRequest[],
  queries: ParameterSeriesQuerySnapshot[],
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[],
): LocationMarkerLoadState {
  for (const parameterId of configuredParameterIds) {
    if (!locationSupportsParameter(location, parameterId)) continue;

    const unitCode = resolveFetchUnitForParameter(parameterId, timeSeriesConfigurations);
    const index = findSeriesRequestIndex(requests, location.id, parameterId, unitCode);
    const query = index >= 0 ? queries[index] : undefined;
    if (!query || query.isPending) return "loading";
  }
  return "ready";
}

/** Show when the station supports at least one chart parameter; hide otherwise. */
export function shouldShowMarker(supportsAnyConfiguredChartParameter: boolean): boolean {
  return supportsAnyConfiguredChartParameter;
}

export function getMarkerSeriesAggregateStatus(
  queries: ParameterSeriesQuerySnapshot[],
): MarkerSeriesAggregateStatus {
  return {
    anyPending: queries.some((query) => query.isPending),
    anyError: queries.some(
      (query) =>
        query.isError ||
        (query.data != null && isObservedTimeSeriesLoadFailure(query.data)),
    ),
  };
}

function markerLoadErrorCell(
  parameterId: string,
  resolveParameterLabel: (parameterId: string) => string | undefined,
): MarkerParameterCell {
  return {
    kind: "error",
    parameterLabel: resolveMarkerParameterLabel(parameterId, resolveParameterLabel),
  };
}

function resolveMarkerParameterLabel(
  parameterId: string,
  resolveParameterLabel: (parameterId: string) => string | undefined,
): string {
  return resolveParameterLabel(parameterId) ?? parameterId;
}
